import clsx from 'clsx'
import React from 'react'
import useIntl from 'react-intl/src/components/useIntl'

import { CapacityModeEnum } from './capacity.enum'
import { Icon } from '../../atoms'
import { useTravelTypeContext } from '../../contexts'
import { TravelTypeEnum } from '../../../lib/graphql'

const Capacity = ({
  beds,
  bedrooms,
  capacity,
  bathrooms,
  className = '',
  subClassName = '',
  dailyMaxCapacity,
  corporateEveningEventMaxCapacity,
  numberOfWorkspaces,
  mode = CapacityModeEnum.DEFAULT,
  isMapMode = false,
  ...props
}: {
  beds: number
  bedrooms: number
  capacity: number
  bathrooms?: number
  className?: string
  subClassName?: string
  dailyMaxCapacity?: number
  corporateEveningEventMaxCapacity?: number
  numberOfWorkspaces?: number
  mode?: CapacityModeEnum
  travelType?: TravelTypeEnum
  isMapMode?: boolean
}) => {
  const { travelType: travelTypeFromContext } = useTravelTypeContext()
  const travelType = props.travelType ?? travelTypeFromContext

  const intl = useIntl()

  const renderBedrooms = bedrooms
    ? intl.formatMessage(
        {
          id: 'ui.Capacity.bedroomsNumber',
          defaultMessage:
            '{bedrooms} {bedrooms, plural, one {chambre} other {chambres}}',
        },
        { bedrooms },
      )
    : undefined
  const renderBeds = beds
    ? intl.formatMessage(
        {
          id: 'ui.Capacity.bedsNumber',
          defaultMessage: '{beds} {beds, plural, one {lit} other {lits}}',
        },
        { beds },
      )
    : undefined

  const renderBathrooms = bathrooms
    ? intl.formatMessage(
        {
          id: 'ui.Capacity.bathroomsNumber',
          defaultMessage:
            '{bathrooms} {bathrooms, plural, one {salle de bain} other {salles de bain}}',
        },
        { bathrooms },
      )
    : undefined
  const renderWorkspaces = numberOfWorkspaces
    ? intl.formatMessage(
        {
          id: 'ui.Capacity.numberOfWorkspacesNumber',
          defaultMessage:
            '{numberOfWorkspaces} {numberOfWorkspaces, plural, one {salle de travail} other {salles de travail}}',
        },
        { numberOfWorkspaces },
      )
    : undefined
  const renderCapacity = capacity
    ? intl.formatMessage(
        {
          id: 'ui.Capacity.capacityNumber',
          defaultMessage:
            '{capacity} {capacity, plural, one {voyageur} other {voyageurs}}',
        },
        { capacity },
      )
    : undefined
  const renderDailyMaxCapacity = dailyMaxCapacity
    ? intl.formatMessage(
        {
          id: 'ui.Capacity.dailyMaxCapacityNumber',
          defaultMessage:
            '{dailyMaxCapacity} {dailyMaxCapacity, plural, one {personne} other {personnes}}',
        },
        { dailyMaxCapacity },
      )
    : undefined

  const renderCorporateEveningEventMaxCapacity = intl.formatMessage(
    {
      id: 'ui.Capacity.corporateEveningEventMaxCapacityNumber',
      defaultMessage: '{corporateEveningEventMaxCapacity} maximum',
    },
    {
      corporateEveningEventMaxCapacity:
        corporateEveningEventMaxCapacity ?? 'N/A',
    },
  )

  const defaultRender = [
    renderCapacity,
    renderBeds,
    renderBedrooms,
    renderBathrooms,
  ]
    .filter(Boolean)
    .join(' | ')

  if (mode === CapacityModeEnum.HOUSE_SEARCH) {
    return (
      <div className={className}>
        {travelType === TravelTypeEnum.DAILY_TRIP ? (
          <span className={subClassName}>
            {renderWorkspaces}
            {dailyMaxCapacity && numberOfWorkspaces ? ' | ' : ''}
            {renderDailyMaxCapacity}
          </span>
        ) : travelType === TravelTypeEnum.CORPORATE_EVENING_EVENT ? (
          <p className={clsx('flex items-center', subClassName)}>
            <Icon
              name='user-filled'
              className='mb-[1px] mr-[-2px] align-middle'
              size={16}
            />
            <span className='px-1.5'>
              {renderCorporateEveningEventMaxCapacity}
            </span>
          </p>
        ) : (
          <p
            className={clsx(
              isMapMode ? 'block' : 'flex items-center',
              subClassName,
              'gap-0.5',
            )}
          >
            <Icon
              name='user-filled'
              className='mb-[1px] mr-[-2px] align-middle'
              size={16}
            />

            {defaultRender.split(' | ').map((item, index) => (
              <React.Fragment key={index}>
                <span className='whitespace-nowrap'>{item}</span>
                {index < defaultRender.split(' | ').length - 1 && (
                  <span> | </span>
                )}
              </React.Fragment>
            ))}
          </p>
        )}
      </div>
    )
  }

  return (
    <div className={className}>
      {travelType === TravelTypeEnum.DAILY_TRIP ? (
        <span className={subClassName}>
          {renderWorkspaces}
          {dailyMaxCapacity && numberOfWorkspaces ? ' | ' : ''}
          {renderDailyMaxCapacity}
        </span>
      ) : travelType === TravelTypeEnum.CORPORATE_EVENING_EVENT ? (
        <p className={clsx('flex items-center', subClassName)}>
          <Icon
            name='user-filled'
            className='mb-[1px] mr-[-2px] align-middle'
            size={16}
          />
          <span className={subClassName}>
            {renderCorporateEveningEventMaxCapacity}
          </span>
        </p>
      ) : (
        <span className={subClassName}>{defaultRender}</span>
      )}
    </div>
  )
}

export default Capacity
